/**
 * SetupGameOverlay.tsx — Create-game form (task H02).
 *
 * Fields: gift budget (amount → cents), end date (YYYY-MM-DD calendar date),
 * secret persons per player (1 or 2). Client-side validation mirrors the
 * shared contract for instant feedback; the SERVER remains authoritative —
 * API errors (e.g. the 5-active-created-games limit, endDate before the
 * application date) are shown verbatim.
 *
 * On success the created game list item is handed back so the caller can
 * open the game immediately. Dialog behavior (focus trap, Escape, focus
 * return, ARIA) comes from the shared K01 Modal.
 */
import { useRef, useState, type FormEvent } from 'react';
import {
  ApiClientError,
  isoDateSchema,
  type CreateGameResponseData,
} from '@shared';
import { apiV1 } from '../api';
import { Modal } from './Modal';

interface SetupGameOverlayProps {
  onClose: () => void;
  onCreated: (game: CreateGameResponseData) => void;
}

export function SetupGameOverlay({ onClose, onCreated }: SetupGameOverlayProps) {
  const [gameName, setGameName] = useState('');
  const [budgetInput, setBudgetInput] = useState('');
  const [endDate, setEndDate] = useState('');
  const [secretPersonCount, setSecretPersonCount] = useState<1 | 2>(1);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // K02: synchronous duplicate-submit guard (state can't stop a same-tick
  // second click; the ref can).
  const submittingRef = useRef(false);

  /** Client-side mirror of the shared contract (server re-validates). */
  function validate(): string | null {
    const name = gameName.trim();
    if (name.length < 1 || name.length > 64) {
      return 'Game name is required (1-64 characters).';
    }
    const amount = Number.parseFloat(budgetInput);
    const cents = Number.isFinite(amount) ? Math.round(amount * 100) : NaN;
    if (!Number.isFinite(cents) || cents < 1) {
      return 'Gift budget must be a positive amount.';
    }
    if (endDate === '') {
      return 'End date is required.';
    }
    const date = isoDateSchema.safeParse(endDate);
    if (!date.success) {
      return date.error.issues[0]?.message ?? 'End date is invalid.';
    }
    return null;
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (submittingRef.current) return;
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const cents = Math.round(Number.parseFloat(budgetInput) * 100);
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const game = await apiV1.createGame({
        name: gameName.trim(),
        giftBudgetCents: cents,
        endDate,
        secretPersonCount,
      });
      onCreated(game);
      // Success: the caller closes the overlay; clear the guard so a later
      // reopened overlay (same mounted instance) can submit again.
      submittingRef.current = false;
    } catch (err) {
      // Safe, non-secret API messages (validation, 5-game limit, …).
      setError(err instanceof ApiClientError ? err.message : 'Could not create the game. Try again.');
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Set up a new game" onClose={onClose} blocked={submitting}>
        <form className="overlay-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label>
            Game name
            <input
              type="text"
              maxLength={64}
              placeholder="Winter exchange"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              disabled={submitting}
            />
          </label>

          <label>
            Gift budget
            <input
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              placeholder="500"
              value={budgetInput}
              onChange={(e) => setBudgetInput(e.target.value)}
              disabled={submitting}
            />
          </label>

          <label>
            End date
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={submitting}
            />
          </label>

          <fieldset className="secret-persons" disabled={submitting}>
            <legend>Secret persons per player</legend>
            <label>
              <input
                type="radio"
                name="secretPersonCount"
                value="1"
                checked={secretPersonCount === 1}
                onChange={() => setSecretPersonCount(1)}
              />{' '}
              1 person
            </label>
            <label>
              <input
                type="radio"
                name="secretPersonCount"
                value="2"
                checked={secretPersonCount === 2}
                onChange={() => setSecretPersonCount(2)}
              />{' '}
              2 persons
            </label>
          </fieldset>

          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}

          <div className="overlay-actions">
            <button type="button" className="game-refresh" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="auth-submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create game'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
